import React, { useState, useEffect, useRef } from 'react';
import { Mail, Github, Linkedin } from 'lucide-react';
import VisitCounter from './components/VisitCounter';
import './App.css';

const JOBS = [
    {
        id: 'ridgeline',
        years: '2020 - Present',
        company: 'Ridgeline',
        url: 'https://www.ridgelineapps.com/',
        title: 'Senior Engineering Manager',
        current: true,
        roles: [
            'Senior Engineering Manager, Portfolio Accounting (Sep 2026 - Present)',
            'Engineering Manager, Portfolio Accounting (Oct 2024 - Sep 2026)',
            'Staff Engineer, Real-Time Pricing (Aug 2023 - Oct 2024)',
            'Senior Product Engineer/Tech Lead, Market Data (Jun 2020 - Aug 2023)',
        ],
    },
    {
        id: 'alchemy',
        years: '2018',
        company: 'Alchemy',
        url: 'https://www.alchemy.us/',
        title: 'Product Engineer',
        roles: ['Product Engineer, Mobile Applications (Jan 2018 - Apr 2020)'],
    },
    {
        id: 'igt',
        years: '2016',
        company: 'IGT',
        url: 'https://www.igt.com/',
        title: 'Software Engineer I',
        roles: ['Software Engineer I, Platform (Jul 2016 - Dec 2017)'],
    },
];

function useFadeIn() {
    const ref = useRef(null);
    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        const observer = new IntersectionObserver(
            ([entry]) => { if (entry.isIntersecting) { el.classList.add('visible'); observer.unobserve(el); } },
            { threshold: 0.1 }
        );
        observer.observe(el);
        return () => observer.disconnect();
    }, []);
    return ref;
}

function TimelineItem({ job, expanded, onToggle }) {
    const panelId = `roles-${job.id}`;
    return (
        <div className={`timeline-item ${job.current ? 'current' : ''}`}>
            <div className="timeline-year">{job.years}</div>
            <div className="timeline-dot"></div>
            <div className={`timeline-content ${expanded ? 'expanded' : ''}`} onClick={onToggle}>
                <a
                    href={job.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="timeline-company"
                    onClick={(e) => e.stopPropagation()}
                >
                    {job.company}
                </a>
                {/* Enter/Space on the button fires a click that bubbles to the card's onToggle. */}
                <button
                    type="button"
                    className="timeline-title"
                    aria-expanded={expanded}
                    aria-controls={panelId}
                >
                    {job.title}
                </button>
                <div id={panelId} className={`timeline-progression ${expanded ? 'expanded' : ''}`}>
                    <div className="timeline-progression-inner">
                        {job.roles.map((role) => (
                            <div key={role} className="progression-item">{role}</div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}

function App() {
    const [expandedTimeline, setExpandedTimeline] = useState(null);

    const toggleTimeline = (company) => {
        setExpandedTimeline(expandedTimeline === company ? null : company);
    };

    const aboutRef = useFadeIn();
    const workRef = useFadeIn();
    const educationRef = useFadeIn();
    const contactRef = useFadeIn();

    return (
        <div className="App">
            <div className="container">
                {/* Header Section */}
                <header className="section">
                    <h1>Wesley Kepke</h1>
                </header>

                {/* About Section */}
                <section ref={aboutRef} className="section fade-in-section">
                    <h2>About</h2>
                    <p>
                        I'm an engineering manager/tech leader and freeride skier based in Reno, NV.
                    </p>
                    <p>
                        At Ridgeline, I build high-performance, scalable, and secure financial systems while leading cross-functional teams.
                    </p>
                </section>

                {/* Work History Section */}
                <section ref={workRef} className="section fade-in-section">
                    <h2>Work</h2>
                    <div className="timeline">
                        <div className="timeline-line"></div>
                        {JOBS.map((job) => (
                            <TimelineItem
                                key={job.id}
                                job={job}
                                expanded={expandedTimeline === job.id}
                                onToggle={() => toggleTimeline(job.id)}
                            />
                        ))}
                    </div>
                </section>

                {/* Education Section */}
                <section ref={educationRef} className="section fade-in-section">
                    <h2>Education</h2>
                    <div className="education-card">
                        <div className="education-year">2016</div>
                        <div className="education-content">
                            <a href="https://www.unr.edu/cse" target="_blank" rel="noopener noreferrer" className="timeline-company">University of Nevada, Reno</a>
                            <div className="education-degree">Bachelor of Science, Computer Science &mdash; <em>cum laude</em></div>
                        </div>
                    </div>
                </section>

                {/* Contact Section */}
                <section ref={contactRef} className="section fade-in-section">
                    <h2>Contact</h2>
                    <div className="contact-links">
                        <a href="mailto:wkepke@protonmail.com"><Mail size={15} strokeWidth={1.5} /><span>Email</span></a>
                        <a href="https://github.com/wesleykepke" target="_blank" rel="noopener noreferrer"><Github size={15} strokeWidth={1.5} /><span>GitHub</span></a>
                        <a href="https://www.linkedin.com/in/wesleykepke/" target="_blank" rel="noopener noreferrer"><Linkedin size={15} strokeWidth={1.5} /><span>LinkedIn</span></a>
                    </div>
                </section>
            </div>

            {/* Visit Counter */}
            <VisitCounter />
        </div>
    );
}

export default App;
